import { Button } from '@chr/eds-react';
import React, { useEffect } from 'react';
import ReactJoyride, { ACTIONS, CallBackProps, EVENTS, STATUS, Step } from 'react-joyride';
import './GuidedTour.scss';
import { IconButton, Icon } from '@mui/material';
import { useTranslation } from '@chr/online-shared-ui-localizations';
import useTabStore from '@/common/stores/tab-store';
import { useAnalytics } from '@/common/hooks/useAnalytics';

interface IGuidedTourProps {
  steps: Step[];
}

const GuidedTour = (props: IGuidedTourProps) => {
  const [run, setRun] = React.useState(false);
  const [stepIndex, setStepIndex] = React.useState(0);
  const { setActiveTab } = useTabStore();
  const localStorageKey = 'booking-details-user-finished-guided-tour';

  const firstTimeUserValue = '1';

  useEffect(() => {
    const firstTimeUser = localStorage.getItem(localStorageKey);
    setRun(firstTimeUser !== firstTimeUserValue);
  });

  const userFinishedGuidedTour = () => {
    localStorage.setItem(localStorageKey, firstTimeUserValue);
  };

  const analytics = useAnalytics();

  const handleOverlayClick = (index: number) => {
    // Ignore if the click occurred in the highlighted area.
    const spotlight = document.querySelector('div.react-joyride__spotlight');
    if (spotlight?.matches(':hover') === false) {
      analytics.trackButtonClick('whats-new-overlay');
      setStepIndex(index);
      setRun(false);
      userFinishedGuidedTour();
    }
  }

  const handleJoyrideCallback = (data: CallBackProps) => {
    const { action, index, status, type } = data;

    // This allows the guided tour to exit if the background overlay gets clicked.
    document.querySelector('div.react-joyride__overlay')?.addEventListener('click', () => handleOverlayClick(index));

    if (([STATUS.FINISHED, STATUS.SKIPPED] as string[]).includes(status)) {
      setStepIndex(0);
      setRun(false);
      userFinishedGuidedTour();
    } else if (([EVENTS.STEP_AFTER, EVENTS.TARGET_NOT_FOUND] as string[]).includes(type)) {
      const nextStepIndex = index + (action === ACTIONS.PREV ? -1 : 1);
      const activeTab = nextStepIndex == 5 ?  'documents' : 'tracking';
      setActiveTab(activeTab);
      setStepIndex(nextStepIndex);
    }
  };

  return run ? (
    <ReactJoyride
      steps={props.steps}
      run={run}
      stepIndex={stepIndex}
      callback={handleJoyrideCallback}
      tooltipComponent={CustomToggletip}
      styles={{
        options: {
          arrowColor: '#0071c7',
          zIndex: 9999
        },
      }}
    />
  ) : (
    <></>
  );
};

const CustomToggletip = (props: any) => {
  const currentStep = props.index + 1;
  const translate = useTranslation();
  const nextBtnText = currentStep === props.size ? translate('Finish') : translate('Next');

  return (
    <div className="toggle-tip-box" data-testid="toggletip">
      <div className="header">
        <h1>{props.step.title}</h1>
        <IconButton {...props.skipProps} id="guided-tour-close" data-testid="guided-tour-close">
          <Icon sx={{ fontSize: 24, color: '#FFFFFF' }}>close</Icon>
        </IconButton>
      </div>

      <div className="content">{props.step.content}</div>

      <div className="footer">
        <div>{props.index > 0 && <Button {...props.backProps} buttonText="Back"></Button>}</div>

        <div>
          {currentStep} of {props.size}
        </div>

        <Button
          {...props.primaryProps}
          buttonText={nextBtnText}
          data-testid="toggleTip-next"
        ></Button>
      </div>
    </div>
  );
};

export default GuidedTour;